import { defineComponent as _defineComponent } from 'vue';
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useMemoize, watchImmediate } from "@vueuse/core";
import { BButton } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import { loadWorkflows } from "@/components/Workflow/workflows.services";
import { useAnimationFrameScroll } from "@/composables/sensors/animationFrameScroll";
import { useToast } from "@/composables/toast";
import ActivityPanel from "./ActivityPanel.vue";
import FavoritesButton from "./Buttons/FavoritesButton.vue";
import DelayedInput from "@/components/Common/DelayedInput.vue";
import ScrollToTopButton from "@/components/ToolsList/ScrollToTopButton.vue";
import WorkflowCardList from "@/components/Workflow/List/WorkflowCardList.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowPanel',
    props: {
        currentWorkflowId: null
    },
    emits: ["insertWorkflow", "insertWorkflowSteps", "createWorkflow"],
    setup(__props, { emit }) {
        const props = __props;
        const scrollable = ref(null);
        const { arrived, scrollTop } = useAnimationFrameScroll(scrollable);
        const loading = ref(false);
        const totalWorkflowsCount = ref(Infinity);
        const allLoaded = computed(() => totalWorkflowsCount.value <= workflows.value.length);
        const filterText = ref("");
        const workflows = ref([]);
        const showFavorites = computed({
            get() {
                return filterText.value.includes("is:bookmarked");
            },
            set(value) {
                if (value) {
                    if (!filterText.value.includes("is:bookmarked")) {
                        filterText.value = `is:bookmarked ${filterText.value}`.trim();
                    }
                }
                else {
                    filterText.value = filterText.value.replace("is:bookmarked", "").trim();
                }
            },
        });
        const loadWorkflowsOptions = {
            sortBy: "update_time",
            sortDesc: true,
            limit: 20,
            showPublished: false,
            skipStepCounts: false,
        };
        const { error } = useToast();
        const getWorkflows = useMemoize(async (filterText, offset) => {
            const { data, totalMatches } = await loadWorkflows({
                ...loadWorkflowsOptions,
                offset,
                filterText,
            });
            return { data, totalMatches };
        });
        let fetchKey = "";
        let lastFetchKey = "";
        async function load() {
            const isCurrentFetch = () => fetchKey.trim().toLowerCase() === lastFetchKey.trim().toLowerCase();
            if (isCurrentFetch() && (loading.value || allLoaded.value)) {
                return;
            }
            lastFetchKey = fetchKey;
            loading.value = true;
            try {
                const { data, totalMatches } = await getWorkflows(filterText.value, workflows.value.length);
                if (isCurrentFetch()) {
                    totalWorkflowsCount.value = totalMatches;
                    const workflowIds = new Set(workflows.value.map((w) => w.id));
                    const newWorkflows = data.filter((w) => !workflowIds.has(w.id));
                    workflows.value.push(...newWorkflows);
                }
            }
            catch (e) {
                if (isCurrentFetch()) {
                    error(`Failed to load workflows: ${e}`);
                }
            }
            finally {
                if (isCurrentFetch()) {
                    loading.value = false;
                }
            }
        }
        function resetWorkflows() {
            workflows.value = [];
            totalWorkflowsCount.value = Infinity;
            loading.value = false;
            fetchKey = "";
        }
        function refresh() {
            resetWorkflows();
            getWorkflows.clear();
            load();
        }
        watchImmediate(() => filterText.value, (newFilterText) => {
            showFavorites.value = newFilterText.includes("is:bookmarked");
            resetWorkflows();
            fetchKey = filterText.value;
            load();
        });
        watch(() => arrived.bottom, () => {
            if (arrived.bottom) {
                load();
            }
        });
        function scrollToTop() {
            scrollable.value?.scrollTo({ top: 0, behavior: "smooth" });
        }
        function createNew(event) {
            event.preventDefault();
            emit("createWorkflow");
        }
        return { __sfc: true, props, emit, scrollable, arrived, scrollTop, loading, totalWorkflowsCount, allLoaded, filterText, workflows, showFavorites, loadWorkflowsOptions, error, getWorkflows, fetchKey, lastFetchKey, load, resetWorkflows, refresh, scrollToTop, createNew, faPlus, FontAwesomeIcon, BButton, ActivityPanel, FavoritesButton, DelayedInput, ScrollToTopButton, WorkflowCardList };
    }
});
